import React from 'react'
import { CheckCircleIcon } from '@phosphor-icons/react'
import type { WheelVersionDisplayStatus } from '../../types/GrowthWheel'
import { LockIcon } from '@phosphor-icons/react/dist/csr/Lock'
import { WarningIcon } from '@phosphor-icons/react/dist/csr/Warning'

const statusBadgeClass: Record<WheelVersionDisplayStatus, string> = {
  active: 'gw-version-bar__badge--active',
  draft: 'gw-version-bar__badge--draft',
  historical: 'gw-version-bar__badge--historical',
}

const statusBadgeLabel: Record<WheelVersionDisplayStatus, string> = {
  active: 'Active',
  draft: 'Draft',
  historical: 'Historical',
}

export function formatCreatedDate(iso: string) {
  return new Date(iso).toLocaleDateString()
}

export function VersionStatusBadge({
  status,
}: {
  status: WheelVersionDisplayStatus
}) {
  return (
    <span className={`gw-version-bar__badge ${statusBadgeClass[status]}`}>
      {statusBadgeLabel[status]}
    </span>
  )
}

export function DraftBanner() {
  return (
    <div className="gw-version-bar__banner gw-version-bar__banner--draft">
      <WarningIcon size={16} weight="regular" />
      <div>
        <div className="gw-version-bar__banner-title">
          DRAFT — Not Visible to Contributors
        </div>
        <div className="gw-version-bar__banner-text">
          Edits here are isolated. Activate this version to make it live.
        </div>
      </div>
    </div>
  )
}

export function ActiveBanner() {
  return (
    <div className="gw-version-bar__banner gw-version-bar__banner--active">
      <CheckCircleIcon size={16} weight="regular" />
      <div className="gw-version-bar__banner-text">
        You&apos;re editing the <strong>Active</strong> version. Changes are
        reflected immediately for contributors.
      </div>
    </div>
  )
}

export function HistoricalBanner() {
  return (
    <div className="gw-version-bar__banner gw-version-bar__banner--historical">
      <LockIcon size={16} weight="regular" />
      <div>
        <div className="gw-version-bar__banner-title">Read Only</div>
        <div className="gw-version-bar__banner-text">
          This is a historical version. Editing is disabled.
        </div>
      </div>
    </div>
  )
}

export function WheelVersionStatusBanner({
  status,
}: {
  status: WheelVersionDisplayStatus
}) {
  if (status === 'draft') return <DraftBanner />
  if (status === 'active') return <ActiveBanner />
  return <HistoricalBanner />
}

export function getErrorMessage(err: unknown, fallbackMessage: string) {
  const data =
    err &&
    typeof err === 'object' &&
    'data' in err &&
    err.data &&
    typeof err.data === 'object'
      ? (err.data as Record<string, unknown>)
      : null
  return (
    (typeof data?.error === 'string' && data.error) ||
    (typeof data?.message === 'string' && data.message) ||
    fallbackMessage
  )
}
